import { ApolloError } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";
import { useTranslation } from "react-i18next";

/**
 * GraphQL error codes that map to translation keys
 */
export const GRAPHQL_ERROR_CODES = {
  // Authentication & Authorization
  INVALID_CREDENTIALS: "invalidCredentials",
  TOKEN_EXPIRED: "tokenExpired",
  TOKEN_INVALID: "tokenInvalid",
  USER_NOT_FOUND: "userNotFound",
  USER_DISABLED: "userDisabled",
  SESSION_EXPIRED: "sessionExpired",
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  INSUFFICIENT_PERMISSIONS: "insufficientPermissions",

  // Validation & Business Logic
  VALIDATION_ERROR: "validationError",
  DUPLICATE_ERROR: "duplicateError",
  NOT_FOUND: "notFound",
  BAD_REQUEST: "badRequest",

  // Appointment-specific
  APPOINTMENT_CONFLICT: "appointmentConflict",
  APPOINTMENT_NOT_FOUND: "appointmentNotFound",
  APPOINTMENT_CANCELLATION_DEADLINE: "appointmentCancellationDeadline",
  BOOKING_WINDOW_CLOSED: "bookingWindowClosed",
  INVALID_TIME_SLOT: "invalidTimeSlot",
  WORKER_SCHEDULE_CONFLICT: "workerScheduleConflict",

  // Customer-specific
  CUSTOMER_EXISTS: "customerExists",
  CUSTOMER_NOT_FOUND: "customerNotFound",
  EMAIL_ALREADY_EXISTS: "emailAlreadyExists",
  PHONE_ALREADY_EXISTS: "phoneAlreadyExists",

  // Worker-specific
  WORKER_UNAVAILABLE: "workerUnavailable",
  WORKER_NOT_FOUND: "workerNotFound",
  WORKER_NOT_ACTIVE_IN_SALON: "workerNotActiveInSalon",
  WORKER_CANNOT_PERFORM_SERVICE: "workerCannotPerformService",

  // Service-specific
  SERVICE_NOT_FOUND: "serviceNotFound",
  SERVICE_CODE_ALREADY_EXISTS: "serviceCodeAlreadyExists",

  // Salon-specific
  SALON_NOT_FOUND: "salonNotFound",

  // AI Agent-specific
  AI_CHAT_NOT_ENABLED: "aiChatNotEnabled",
  AI_CREDITS_EXHAUSTED: "aiCreditsExhausted",
  AI_CHAT_DISABLED: "aiChatDisabled",

  // Technical errors
  INTERNAL_ERROR: "internalError",
  NETWORK_ERROR: "networkError",
  SERVER_UNAVAILABLE: "serverUnavailable",
  RATE_LIMITED: "rateLimited",
  FILE_UPLOAD_ERROR: "fileUploadError",
  PAYMENT_ERROR: "paymentError",
} as const;

export type GraphQLFormattedErrorCode =
  (typeof GRAPHQL_ERROR_CODES)[keyof typeof GRAPHQL_ERROR_CODES];

/**
 * Localized error information
 */
export interface LocalizedError {
  message: string;
  code?: string;
  field?: string;
  originalError?: GraphQLFormattedError;
}

/**
 * Hook to handle GraphQL errors with localization
 */
export function useGraphQLFormattedErrorHandler() {
  const { t } = useTranslation("errors");

  /**
   * Convert a GraphQL error code to a localized message
   */
  const getLocalizedMessage = (
    errorCode: string,
    fallbackMessage?: string,
    interpolations?: Record<string, string | number | Record<string, string>>
  ): string => {
    const translationKey = `graphql.${errorCode}`;
    const translation = t(translationKey, {
      defaultValue: fallbackMessage || t("general.unknownError"),
      ...interpolations,
    });

    // If translation key wasn't found and we don't have a fallback, use general unknown error
    if (translation === translationKey && !fallbackMessage) {
      return t("general.unknownError");
    }

    return translation;
  };

  /**
   * Extract error code from GraphQL error extensions
   */
  const extractErrorCode = (error: GraphQLFormattedError): string | undefined => {
    return error.extensions?.code as string;
  };

  const extractTextMetadata = (error: GraphQLFormattedError): Record<string, string> => {
    const metadata = error.extensions?.metadata;
    if (typeof metadata !== "object" || metadata === null) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(metadata).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string"
      )
    );
  };

  /**
   * Extract field name from GraphQL error path
   */
  const extractFieldName = (error: GraphQLFormattedError): string | undefined => {
    if (error.path && error.path.length > 0) {
      return error.path[error.path.length - 1]?.toString();
    }
    return undefined;
  };

  /**
   * Process a single GraphQL error
   */
  const processGraphQLFormattedError = (error: GraphQLFormattedError): LocalizedError => {
    const errorCode = extractErrorCode(error);
    const field = extractFieldName(error);

    if (errorCode) {
      const message = getLocalizedMessage(errorCode, error.message, {
        metadata: extractTextMetadata(error),
      });
      return {
        message,
        code: errorCode,
        field,
        originalError: error,
      };
    }

    // Fallback to original message or unknown error
    return {
      message: error.message || t("general.unknownError"),
      field,
      originalError: error,
    };
  };

  /**
   * Process network errors
   */
  const processNetworkError = (networkError: Error): LocalizedError => {
    // Check for specific network error types
    if ("statusCode" in networkError) {
      const statusCode = (networkError as { statusCode: number }).statusCode;

      switch (statusCode) {
        case 401:
          return {
            message: getLocalizedMessage("unauthorized"),
            code: "unauthorized",
          };
        case 403:
          return {
            message: getLocalizedMessage("forbidden"),
            code: "forbidden",
          };
        case 404:
          return {
            message: getLocalizedMessage("notFound"),
            code: "notFound",
          };
        case 429:
          return {
            message: getLocalizedMessage("rateLimited"),
            code: "rateLimited",
          };
        case 500:
          return {
            message: getLocalizedMessage("internalError"),
            code: "internalError",
          };
        case 503:
          return {
            message: getLocalizedMessage("serverUnavailable"),
            code: "serverUnavailable",
          };
        default:
          return {
            message: getLocalizedMessage("networkError"),
            code: "networkError",
          };
      }
    }

    // Check for connection errors
    if (
      networkError.message.toLowerCase().includes("network") ||
      networkError.message.toLowerCase().includes("fetch")
    ) {
      return {
        message: getLocalizedMessage("networkError"),
        code: "networkError",
      };
    }

    // Fallback to general network error
    return {
      message: getLocalizedMessage("networkError", networkError.message),
      code: "networkError",
    };
  };

  /**
   * Main error processing function
   */
  const processApolloError = (apolloError: ApolloError): LocalizedError[] => {
    const errors: LocalizedError[] = [];

    // Process GraphQL errors
    if (apolloError.graphQLErrors && apolloError.graphQLErrors.length > 0) {
      for (const graphQLError of apolloError.graphQLErrors) {
        errors.push(processGraphQLFormattedError(graphQLError));
      }
    }

    // Process network error
    if (apolloError.networkError) {
      errors.push(processNetworkError(apolloError.networkError));
    }

    // If no specific errors were found, return a general error
    if (errors.length === 0) {
      errors.push({
        message: apolloError.message || t("general.unknownError"),
      });
    }

    return errors;
  };

  /**
   * Get the first (primary) error message from an ApolloError
   */
  const getPrimaryErrorMessage = (apolloError: ApolloError): string => {
    const errors = processApolloError(apolloError);
    return errors[0]?.message || t("general.unknownError");
  };

  /**
   * Get all error messages from an ApolloError
   */
  const getAllErrorMessages = (apolloError: ApolloError): string[] => {
    const errors = processApolloError(apolloError);
    return errors.map((error) => error.message);
  };

  /**
   * Check if an error is a specific type
   */
  const isErrorType = (apolloError: ApolloError, errorCode: GraphQLFormattedErrorCode): boolean => {
    const errors = processApolloError(apolloError);
    return errors.some((error) => error.code === errorCode);
  };

  /**
   * Get localized validation error messages for forms
   */
  const getValidationErrors = (apolloError: ApolloError): Record<string, string> => {
    const errors = processApolloError(apolloError);
    const validationErrors: Record<string, string> = {};

    for (const error of errors) {
      if (error.field && error.code === "validationError") {
        validationErrors[error.field] = error.message;
      }
    }

    return validationErrors;
  };

  return {
    processApolloError,
    getPrimaryErrorMessage,
    getAllErrorMessages,
    isErrorType,
    getValidationErrors,
    getLocalizedMessage,
    processGraphQLFormattedError,
    processNetworkError,
  };
}

/**
 * Error boundary helper for GraphQL errors
 */
export function useGraphQLFormattedErrorBoundary() {
  const { getPrimaryErrorMessage, isErrorType } = useGraphQLFormattedErrorHandler();

  const handleError = (error: ApolloError, onError?: (message: string) => void) => {
    const message = getPrimaryErrorMessage(error);

    // Don't show auth errors since they're handled by auth error link
    if (
      isErrorType(error, "tokenExpired") ||
      isErrorType(error, "tokenInvalid") ||
      isErrorType(error, "unauthorized")
    ) {
      return;
    }

    if (onError) {
      onError(message);
    } else {
      // Default error handling (could be toast, console.error, etc.)
      console.error("GraphQL Error:", message);
    }
  };

  return { handleError };
}

/**
 * React Hook Form integration for GraphQL validation errors
 */
export function useGraphQLFormErrors() {
  const { getValidationErrors } = useGraphQLFormattedErrorHandler();

  const setFormErrors = (
    apolloError: ApolloError,
    setError: (name: string, error: { type: string; message: string }) => void
  ) => {
    const validationErrors = getValidationErrors(apolloError);

    for (const [field, message] of Object.entries(validationErrors)) {
      setError(field, {
        type: "server",
        message,
      });
    }
  };

  return { setFormErrors };
}
